<script lang="ts">
  import { onDestroy } from "svelte";
  import { copy } from "../../lib/stores/copy";
  import UploadWhite from "../../assets/upload-white.svg";
  import { resizeImageFile, flipImageHorizontally } from "../../lib/helpers/imageResize";
  import { uploadReceipt } from "../../lib/helpers/uploadReceipt";

  let dragging = false;

  let file: File | undefined;
  let Input: HTMLInputElement;
  let isPreview = false;
  let invalidMessage: string | undefined;
  let previewUrl: string | undefined;

  // Update preview URL whenever file changes
  $: {
    // Revoke old URL to prevent memory leak
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      previewUrl = undefined;
    }
    // Create new URL if file exists
    if (file) {
      previewUrl = URL.createObjectURL(file);
    }
  }

  // Clean up on component destroy
  onDestroy(() => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
  });

  const handleFileSelection = async (selectedFile: File | undefined) => {
    if (selectedFile) {
      try {
        // Resize the image to ensure it meets size limits
        file = await resizeImageFile(selectedFile);
        isPreview = true;
      } catch (error) {
        console.error('Error resizing image:', error);
        invalidMessage = $copy.receipt.rejectedFile;
        file = undefined;
        isPreview = false;
      }
    } else {
      file = undefined;
      isPreview = false;
    }
  };

  const inputChange = async (ev: Event) => {
    const files = (ev.target as HTMLInputElement).files;
    const selectedFile = files && files.length > 0 ? files[0] : undefined;
    await handleFileSelection(selectedFile);
  };

  const dragInputChange = async (ev: DragEvent) => {
    ev.preventDefault();
    ev.stopPropagation();

    dragging = false;

    const files = ev.dataTransfer?.files;
    const selectedFile = files && files.length > 0 ? files[0] : undefined;
    await handleFileSelection(selectedFile);
  };

  const flipImage = async () => {
    if (!file) return;

    try {
      const flippedBlob = await flipImageHorizontally(file, 0.8);
      file = new File([flippedBlob], file.name, { type: 'image/jpeg' });
    } catch (error) {
      console.error('Error flipping image:', error);
    }
  };

  const upload = async () => {
    if (!file) return;

    try {
      await uploadReceipt(file);
    } catch (error) {
      // Handle component-specific cleanup
      file = undefined;
      invalidMessage = $copy.receipt.rejectedFile;
      isPreview = false;
    }
  };
</script>

<form class="max-w-[1100px] mx-auto" class:hidden={isPreview}>
  <div class="font-MrEavesXLSanNarOTUltra text-primary text-center font-black mt-8 leading-[0.9] uppercase text-[24px] mb-4 mobileS:text-[32px] tablet:text-[38px] laptop:text-[42px] desktopLg:text-[48px]">
    {$copy.receipt.uploadFromDevice}
  </div>
  {#if invalidMessage}
    <div class="text-white p-[1rem] rounded-lg text-center font-MrEavesXLSanNarOT mb-[1rem] text-[25px] bg-[#f007]">{invalidMessage}</div>
  {/if}
  <div class="flex-col flex justify-between tabletLg:flex-row">
    <label 
      class:outline-primary={dragging}
      class:outline={dragging}
      class:border-solid={dragging}
      class:border-primary={dragging}
      class:border-dashed={!dragging}
      class:border-white={!dragging}
      on:dragover|preventDefault={() => dragging = true}
      on:dragleave|preventDefault={() => dragging = false}
      on:drop|preventDefault={dragInputChange}
      class="w-[100%] max-w-[540px] mx-auto mb-[48px] relative flex flex-col items-center justify-center h-[320px] border-2 tabletLg:w-[48%] tabletLg:mb-0">
      <input
        type="file"
        id="fileInput"
        bind:this={Input}
        class="w-full h-full opacity-0 absolute"
        on:change={inputChange}
        accept="image/png,image/jpeg"
      >
      <div class="w-[48px] h-[48px] mb-[24px] pointer-events-none">
        <img src={UploadWhite} alt="">
      </div>
      <div class="font-MrEavesXLSanNarOTUltra text-white text-center font-black leading-[0.9] uppercase text-[16px] mb-[24px] mobileS:text-[20px] tablet:text-[24px] laptop:text-[26px] desktop:text-[30px] desktopLg:text-[37px] pointer-events-none">
        {$copy.receipt.dragFileToUpload}
      </div>
      <button
        class="std-button font-MrEavesXLSanNarOTUltra relative inline-flex justify-center items-center text-[18px] text-white px-[32px] uppercase h-[48px] min-w-[132px] bg-dark border-solid border-white border-2 no-underline uppercase transition cursor-pointer overflow-hidden"
        on:click|preventDefault={() => Input.click()}
      >
        <span class="relative z-[1]">
          {$copy.receipt.chooseFile}
        </span>
      </button>
    </label>
  </div>
</form>
{#if isPreview}
  <form class="max-w-[800px] mx-auto">
    <div class="font-MrEavesXLSanNarOTUltra text-primary text-center font-black mt-8 leading-[0.9] uppercase text-[24px] mb-4 mobileS:text-[32px] tablet:text-[38px] laptop:text-[42px] desktopLg:text-[48px]">
      {$copy.reviewReceipt.receiptPreview}
    </div>
    <div class="flex-col flex justify-between tabletLg:flex-row">
      <div class="max-w-[640px] mx-auto">
        <img src={previewUrl} alt="" class="w-full h-full object-cover">
      </div>
    </div>
    <div class="flex flex-col gap-2 tablet:gap-4 mt-4 tablet:flex-row tablet:justify-center tablet:gap-2">
      <button
        type="button"
        class="std-button font-MrEavesXLSanNarOTUltra relative inline-flex justify-center items-center text-[16px] text-white px-[24px] uppercase h-[48px] w-full tablet:min-w-[120px] tablet:w-auto bg-dark border-solid border-white border-2 no-underline uppercase transition cursor-pointer overflow-hidden"
        on:click={() => isPreview = false}
      >
        <span class="relative z-[1]">
          {$copy.reviewReceipt.backButton}
        </span>
      </button>
      <button
        type="button"
        class="std-button font-MrEavesXLSanNarOTUltra relative inline-flex justify-center items-center text-[16px] text-white px-[24px] uppercase h-[48px] w-full tablet:min-w-[120px] tablet:w-auto bg-dark border-solid border-white border-2 no-underline uppercase transition cursor-pointer overflow-hidden"
        on:click={flipImage}
      >
        <span class="relative z-[1] flex items-center gap-1">
          <span>🔄</span>
          <span>{$copy.reviewReceipt.flipPhoto}</span>
        </span>
      </button>
      <button
        class="std-button font-MrEavesXLSanNarOTUltra relative inline-flex justify-center items-center text-[16px] text-white px-[24px] uppercase h-[48px] w-full tablet:min-w-[120px] tablet:w-auto bg-dark border-solid border-white border-2 no-underline uppercase transition cursor-pointer overflow-hidden"
        on:click|preventDefault={upload}
      >
        <span class="relative z-[1]">
          {$copy.reviewReceipt.submitButton}
        </span>
      </button>
    </div>
  </form>
{/if}
